/**
 * Who is in each group of a custom variable (M4S-5777 X57): the one rule the banner, a filter and
 * a weighting target all read, so "Purchase experience: Positive" means the same people as a banner
 * column, as a filter and as a weighting group. Pure. Spec: docs/pdlc/crosstabs-filters.md §3.4.
 *
 * - A group is a LABEL. Buckets that share a label are one group (anyone in any of them), as the
 *   banner has always drawn them, and the label is the group's id: reordering or inserting a bucket
 *   never moves a filter or a weight to another group, which a positional id would.
 * - Logic and Group Answers variables test each bucket through `evalBucket` (conditions.ts, the one
 *   condition evaluator) under `bannerRulesOf`, the rules a banner column counts under, so a group
 *   counts its own members whatever base the variable's own table uses. Range variables test each
 *   range through `evalRange`.
 * - Consecutive Logic buckets joined by AND (`VariableBucket.nextJoin`, or `bucketJoin` on an older
 *   variable) form a chain, and every group in a chain counts the chain's intersection. That is the
 *   banner's rule since the per-bucket connectors shipped; it moved here unchanged from
 *   `addCustomVarCols`, which now reads it from here.
 * - Formula and Combine variables have no groups here: a Formula is a score, and a Combine's groups
 *   are its questions' answers (`addCombineVarCols`), not buckets.
 *
 * Callers pass variables as compute resolves them (`resolveConditions` over `withOptionCodes`), so
 * a merged survey wave's codes and a comparison's answer order are already attached. Compute passes
 * the variables of the last Run, never the rail's, so a filter on a variable whose edit is pending
 * keeps the committed definition until Run (decision E1).
 *
 * Weighting (X57's other half, the weighting package) imports `withCustomVarAnswers`: it adds one
 * answer key per variable, `customVarAnswerKey(id)`, holding the labels of the groups a respondent
 * is in, so a target keyed by group label reads it like any question's answers. The added keys are
 * a local copy for that step and never reach rows or exports.
 */

import type { CustomVariable } from '@/app/(private)/ai/components/Crosstabs/crosstab-types';

import { bannerRulesOf, evalBucket, evalRange, type Answers } from './conditions';
import { inColumnOrder } from './variable-column-order';

/** One group of a variable and the respondents (indices into the response list) in it. */
export interface VariableColumn {
    label: string;
    members: number[];
}

/** The variable kinds whose groups this module can count: the ones a filter or a weight may use. */
export function hasVariableGroups(
    cv: Pick<CustomVariable, 'kind' | 'buckets' | 'ranges'>,
): boolean {
    if (cv.kind === 'logic' || cv.kind === 'categorical-bucketing') return cv.buckets.length > 0;
    if (cv.kind === 'bucketing') return cv.ranges.length > 0;
    return false;
}

/** A variable's groups, by label, in the order they first appear. Same-label buckets are one group. */
export function variableGroupLabels(cv: CustomVariable): string[] {
    const labels =
        cv.kind === 'logic' || cv.kind === 'categorical-bucketing'
            ? cv.buckets.map((b) => b.label)
            : cv.kind === 'bucketing'
              ? cv.ranges.map((r) => r.label)
              : [];
    return [...new Set(labels)];
}

/**
 * Each group of `cv` and its members within `domain`, in group order. The banner's columns are
 * exactly these (`addCustomVarCols`). Logic and Group Answers test buckets; every other kind tests
 * its ranges (none for a Formula or a Combine, so no group).
 */
export function variableColumns(
    cv: CustomVariable,
    responses: ReadonlyArray<Answers>,
    domain: readonly number[],
): VariableColumn[] {
    // A banner column counts its own members: the variable's base choice never reaches it.
    const rules = bannerRulesOf(cv);
    const rawSegments =
        cv.kind === 'logic' || cv.kind === 'categorical-bucketing'
            ? cv.buckets.map((b) => ({
                  label: b.label,
                  test: (idx: number) => evalBucket(responses[idx], b, rules),
              }))
            : cv.ranges.map((r) => ({
                  label: r.label,
                  test: (idx: number) => evalRange(responses[idx], cv.sourceQuestionId, r, rules),
              }));

    const segMap = new Map<string, Array<(idx: number) => boolean>>();
    for (const s of rawSegments) {
        const arr = segMap.get(s.label) ?? [];
        arr.push(s.test);
        segMap.set(s.label, arr);
    }
    const segments = Array.from(segMap.entries()).map(([label, tests]) => ({
        label,
        test: (idx: number) => tests.some((t) => t(idx)),
    }));

    // Per-bucket AND/OR connectors (Logic only). Each bucket has an optional nextJoin ('all'=AND,
    // 'any'=OR) for the connector to the next bucket. Consecutive AND-connected buckets form a
    // chain; each bucket in a chain shows the chain intersection. Falls back to the older global
    // bucketJoin when no per-bucket nextJoin is set.
    let chainIntersections: (Set<number> | null)[] | null = null;
    if (cv.kind === 'logic' && segments.length > 1) {
        const hasPerBucketJoin = cv.buckets.some((b) => b.nextJoin !== undefined);
        const globalJoin = cv.bucketJoin ?? 'any';
        // Map each segment (by label) to the first matching original bucket index
        const labelToBucketIdx = new Map<string, number>();
        for (let k = 0; k < cv.buckets.length; k++) {
            if (!labelToBucketIdx.has(cv.buckets[k].label)) {
                labelToBucketIdx.set(cv.buckets[k].label, k);
            }
        }
        // Assign chain IDs: AND connectors keep segments in the same chain
        const chainIdArr: number[] = new Array(segments.length).fill(0);
        let cid = 0;
        for (let i = 0; i < segments.length - 1; i++) {
            chainIdArr[i] = cid;
            const bIdx = labelToBucketIdx.get(segments[i].label) ?? i;
            const connector = hasPerBucketJoin ? (cv.buckets[bIdx]?.nextJoin ?? 'any') : globalJoin;
            if (connector !== 'all') cid++;
        }
        chainIdArr[segments.length - 1] = cid;
        // Compute intersection set for each chain
        const chainMemberSets = new Map<number, Set<number>>();
        for (let i = 0; i < segments.length; i++) {
            const id = chainIdArr[i];
            if (!chainMemberSets.has(id)) {
                chainMemberSets.set(id, new Set(domain.filter((idx) => segments[i].test(idx))));
            } else {
                const existing = chainMemberSets.get(id)!;
                for (const idx of [...existing]) {
                    if (!segments[i].test(idx)) existing.delete(idx);
                }
            }
        }
        // Count how many segments are in each chain
        const chainSizeMap = new Map<number, number>();
        for (const id of chainIdArr) chainSizeMap.set(id, (chainSizeMap.get(id) ?? 0) + 1);
        // Only restrict membership for chains with ≥2 segments (AND-connected)
        chainIntersections = segments.map((_, i) => {
            const id = chainIdArr[i];
            return (chainSizeMap.get(id) ?? 1) > 1 ? (chainMemberSets.get(id) ?? null) : null;
        });
    }

    // BT-2092: shown in the variable's column order, once everyone is counted in bucket order.
    return inColumnOrder(
        cv,
        segments.map((seg, si) => {
            const restrictSet = chainIntersections?.[si] ?? null;
            return {
                label: seg.label,
                members: domain.filter((idx) =>
                    restrictSet ? restrictSet.has(idx) : seg.test(idx),
                ),
            };
        }),
    );
}

/** The answer key a variable's groups are read under: never a survey QID (a QID holds no colon). */
export function customVarAnswerKey(cvId: string): string {
    return `CV:${cvId.trim().toUpperCase()}`;
}

/**
 * For each respondent, the labels of `cv`'s groups they are in, as a set per response index.
 * Respondents in no group get an empty set: they did not "answer" the variable.
 */
export function variableAnswerSets(
    cv: CustomVariable,
    responses: ReadonlyArray<Answers>,
): Set<string>[] {
    const out = responses.map(() => new Set<string>());
    const all = responses.map((_, i) => i);
    for (const col of variableColumns(cv, responses, all)) {
        for (const i of col.members) out[i].add(col.label);
    }
    return out;
}

/**
 * Copies of `responses` with one more answer per variable in `ids`: `customVarAnswerKey(id)` holds
 * the labels of the groups the respondent is in. Variables that are not in `variables`, or have no
 * groups (`hasVariableGroups`), are skipped. The input is never changed.
 */
export function withCustomVarAnswers(
    responses: ReadonlyArray<Answers>,
    variables: readonly CustomVariable[],
    ids: readonly string[],
): Answers[] {
    const out = responses.map((r) => ({ ...r }));
    const byId = new Map(variables.map((v) => [v.id, v]));
    for (const id of new Set(ids)) {
        const cv = byId.get(id);
        if (!cv || !hasVariableGroups(cv)) continue;
        const key = customVarAnswerKey(cv.id);
        variableAnswerSets(cv, responses).forEach((set, i) => {
            out[i][key] = set;
        });
    }
    return out;
}
